<script lang="ts">
  import type { Snippet } from 'svelte';

  /**
   * フレームを 1 枚ずつ送る（components.md §3.1 StepPlayer の最小版）。
   * ← → で 1 ステップ、Home / End で先頭 / 末尾。進行表示は aria-live。
   */
  interface Props {
    count: number;
    step?: number;
    label?: string;
    children: Snippet<[number]>;
    /** ステップごとの印（✓ / ✕ など）。 */
    marks?: (string | null)[];
  }

  let {
    count,
    step = $bindable(0),
    label = 'ステップ操作',
    children,
    marks = [],
  }: Props = $props();

  const last = $derived(Math.max(0, count - 1));
  const clamped = $derived(Math.min(Math.max(step, 0), last));

  function go(i: number) {
    step = Math.min(Math.max(i, 0), last);
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = clamped + 1;
    else if (e.key === 'ArrowLeft') next = clamped - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    e.stopPropagation();
    go(next);
  }
</script>

<div class="player">
  <div class="controls" role="toolbar" aria-label={label} tabindex="-1" {onkeydown}>
    <button type="button" class="btn btn-sm" onclick={() => go(0)} disabled={clamped === 0}
      >最初</button
    >
    <button
      type="button"
      class="btn btn-sm"
      onclick={() => go(clamped - 1)}
      disabled={clamped === 0}
      aria-label="前のステップ">← 前</button
    >
    <span class="pos num" aria-live="polite">ステップ {clamped + 1} / {count}</span>
    <button
      type="button"
      class="btn btn-sm"
      onclick={() => go(clamped + 1)}
      disabled={clamped >= last}
      aria-label="次のステップ">次 →</button
    >
    <button type="button" class="btn btn-sm" onclick={() => go(last)} disabled={clamped >= last}
      >最後</button
    >
  </div>
  {#if marks.length > 0}
    <ol class="marks" aria-label="ステップごとの一致">
      {#each marks as m, i (i)}
        <li>
          <button
            type="button"
            class="mark"
            class:current={i === clamped}
            class:ok={m === '✓'}
            class:ng={m === '✕'}
            aria-label="ステップ {i + 1}{m === '✓' ? '、一致' : m === '✕' ? '、不一致' : ''}"
            aria-current={i === clamped ? 'step' : undefined}
            onclick={() => go(i)}>{i + 1}<span aria-hidden="true">{m ?? ''}</span></button
          >
        </li>
      {/each}
    </ol>
  {/if}
  {@render children(clamped)}
</div>

<style>
  .player {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .controls {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .pos {
    min-width: 8em;
    text-align: center;
    font-size: var(--fs-sm);
    font-variant-numeric: tabular-nums;
  }
  .marks {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .mark {
    min-width: 2.6rem;
    min-height: 2rem;
    padding: 0 var(--space-2);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface);
    color: var(--text);
    font-size: var(--fs-xs);
    font-variant-numeric: tabular-nums;
  }
  .mark.ok {
    border-color: var(--verdict-ac);
    color: var(--ok-text);
    background: var(--ok-bg);
  }
  .mark.ng {
    border-color: var(--verdict-wa);
    color: var(--err-text);
    background: var(--err-bg);
  }
  .mark.current {
    outline: 2px solid var(--focus);
    outline-offset: 1px;
  }
</style>
